import { getChatCompletion } from './aiIntegrations/chatCompletion';

const SYSTEM_PROMPT = `You are an expert Australian and UAE real estate analyst with deep knowledge of property markets, pricing trends, and investment analysis. Your role is to analyze property listings and provide comprehensive, data-driven assessments.

You MUST respond with valid JSON only - no markdown, no extra text, just the JSON object.`;

export function buildPropertyPrompt(propertyData) {
  const { address, price, bedrooms, bathrooms, propertyType, description, region } = propertyData;
  
  return `Analyze this property listing and provide a comprehensive investment analysis.

Property Details:
- Address: ${address || 'Not provided'}
- Asking Price: ${price || 'Not provided'}
- Bedrooms: ${bedrooms || 'Not provided'}
- Bathrooms: ${bathrooms || 'Not provided'}
- Property Type: ${propertyType || 'House'}
- Region: ${region || 'Australia'}
- Description: ${description || 'Not provided'}

Provide your analysis as a JSON object with EXACTLY this structure:
{
  "verdict": {
    "type": "fair" | "overpriced" | "underpriced",
    "summary": "3-4 paragraph detailed explanation of the verdict"
  },
  "strengths": ["strength 1", "strength 2", "strength 3", "strength 4", "strength 5"],
  "risks": ["risk 1", "risk 2", "risk 3", "risk 4", "risk 5"],
  "offerBand": {
    "min": <minimum recommended offer as integer>,
    "max": <maximum recommended offer as integer>
  },
  "questionsForAgent": ["question 1", "question 2", "question 3", "question 4", "question 5"],
  "nextSteps": ["step 1", "step 2", "step 3", "step 4", "step 5"],
  "confidence": {
    "overall": <0-100 integer>,
    "verdict": <0-100 integer>,
    "strengths": <0-100 integer>,
    "risks": <0-100 integer>,
    "offerBand": <0-100 integer>
  }
}

Base your analysis on:
1. The asking price relative to typical market values for this property type and location
2. Property features and their impact on value
3. Market conditions in the specified region
4. Risk factors that buyers should be aware of
5. Realistic offer range based on the analysis

Respond with ONLY the JSON object, no other text.`;
}

export async function analyzeProperty(propertyData) {
  const userPrompt = buildPropertyPrompt(propertyData);
  
  const messages = [
    { role: 'system', content: SYSTEM_PROMPT },
    { role: 'user', content: userPrompt }
  ];

  const result = await getChatCompletion('OPEN_AI', 'gpt-4.1', messages, {
    max_completion_tokens: 2000,
    temperature: 0.7,
    response_format: { type: 'json_object' }
  });

  const content = result?.choices?.[0]?.message?.content || '{}';
  
  try {
    const parsed = JSON.parse(content);
    return {
      ...parsed,
      property: propertyData,
      analysisDate: new Date()?.toISOString(),
      id: `ANL-${Date.now()}`,
      status: 'complete'
    };
  } catch (e) {
    throw new Error('Failed to parse AI analysis response');
  }
}
